import { ZERO, cents, sum } from '../money.js';
import { normaliseName } from '../text.js';
import type { Receipt } from '../types.js';

/**
 * A tip is never an item, whatever the reader called it.
 *
 * Receipts print gratuities among the lines with a quantity and a price, and
 * a model reading them will sometimes list one as a dish. This is the net
 * under the prompt: any line whose name is a tip word — in the languages
 * receipts near us come in, with the spellings and diacritics they actually
 * use — is lifted out of the items and added to tipCents. Deterministic, so
 * it holds even on the day the model has a bad read.
 */
const TIP_WORDS = [
  // English
  'tip', 'tips', 'gratuity', 'gratuities', 'tipamount', 'tipincluded',
  // Romanian: bacșiș, with the ş/ș variants and the ways it is typed
  'bacsis', 'bacsisul', 'bacsish', 'bacsiș', 'bacşiş', 'bacsiş', 'bacșis', 'baksis', 'bacshish',
  // German, Dutch, Scandinavian
  'trinkgeld', 'fooi', 'dricks', 'drikkepenge', 'driks', 'tips', 'juomaraha', 'þjórfé',
  // Romance
  'propina', 'propinas', 'pourboire', 'pourboires', 'mancia', 'mance', 'gorjeta', 'gorjetas', 'bacşiş',
  // Central and Eastern Europe
  'napiwek', 'spropitne', 'spropitné', 'borravalo', 'borravaló', 'bakšiš', 'baksis', 'bakshish', 'napojnica', 'чаевые', 'chaevye', 'бакшиш',
  // Greek, Turkish
  'φιλοδώρημα', 'filodorima', 'bahşiş', 'bahsis',
  // Hebrew, Arabic (as they appear on receipts)
  'טיפ', 'תשר', 'بقشيش', 'اكرامية', 'إكرامية',
  // Asian receipts in English
  'servicetip', 'gratuityamount',
];

const TIP_SET = new Set(TIP_WORDS.map((word) => normaliseName(word)));

/** True when the whole line name is a tip word (after stripping accents and punctuation). */
export function isTipName(name: string): boolean {
  const key = normaliseName(name);
  if (key === '') return false;
  if (TIP_SET.has(key)) return true;
  // "Tip 10%", "Bacsis (optional)", "Gratuity 18%": a tip word followed only by a percentage or a note.
  const head = normaliseName(name.split(/[\s(:%\d]/)[0] ?? '');
  return head !== '' && TIP_SET.has(head) && /^[\p{L}]+[\s(:]*(?:\d{1,3}\s*%)?[\s\p{L}()]*$/u.test(name.trim());
}

/**
 * Move tip lines out of the items and into tipCents. Returns the same
 * receipt object when there is nothing to do, so callers can compare.
 */
export function absorbTipLines(receipt: Receipt): Receipt {
  const tipLines = receipt.items.filter((item) => item.lineTotal > 0 && isTipName(item.name));
  if (tipLines.length === 0) return receipt;
  const moved = sum(tipLines.map((item) => item.lineTotal));
  const tip = cents((receipt.tip ?? ZERO) + moved);
  const items = receipt.items.filter((item) => !tipLines.includes(item));
  // A printed subtotal that included the tip line must lose it too, or the
  // items-vs-subtotal check fires for the very thing we just fixed.
  const subtotal =
    receipt.subtotal !== undefined && Math.abs(receipt.subtotal - sum(receipt.items.map((i) => i.lineTotal))) <= 2
      ? cents(receipt.subtotal - moved)
      : receipt.subtotal;
  return { ...receipt, items, tip, ...(subtotal !== undefined ? { subtotal } : {}) };
}
